// What a tap does on an artifact card or the shot board. Each one calls the same endpoint the Director's tool
// calls, tracks the job it starts, and tells the Director, so voice and touch stay one conversation.
import { api, ApiError } from "./api";
import { toggleAudio } from "./audio";
import { director, openProjectId, switchVoice } from "./director";
import { artifactsOf, withoutCardsOrStage } from "./directives";
import { playNow } from "./player";
import { failureText, paidFailure, type PaidFailure } from "./produce";
import { onJob, sceneIndex } from "./session";
import { bump, showArtifact, toast } from "./store";
import type { PriceStep } from "./price";
import type { Artifact, JobView, LinesEdit, ProduceMode, Project, SceneFit, SfxEdit, SoundMix } from "./types";
import { applyUi } from "./ui";

const label = (projectId: string, sceneId: string | undefined, what: string) => {
  const i = sceneIndex(projectId, sceneId);
  return i ? `scene ${i}'s ${what}` : `the ${what}`;
};

function failed(e: unknown, what: string) {
  const msg = e instanceof ApiError && e.status === 402 ? failureText(e) : `Couldn't ${what}. ${failureText(e)}`;
  toast(msg, "bad");
}

function track(job: JobView, said: string) {
  onJob(job);
  bump("credits");
  director.context(`The user ${said} by tapping (job ${job.id}, about ${job.credits} credits). Don't start it again.`);
  return job;
}

// ------------------------------------------------------------------ shots

export async function makeFrame(projectId: string, sceneId: string, prompt?: string) {
  try {
    return track(await api.makeFrame(projectId, sceneId, prompt ? { prompt } : {}), `started ${label(projectId, sceneId, "frame")}`);
  } catch (e) {
    failed(e, "start the frame");
  }
}

/** `model`: a catalog id; omitted = whatever the project or account uses. */
export async function makeClip(projectId: string, sceneId: string, model?: string, motionPrompt?: string, expectCredits?: number) {
  try {
    return track(
      await api.makeClip(projectId, sceneId, { ...(model ? { model } : {}), ...(motionPrompt ? { motionPrompt } : {}), ...(expectCredits !== undefined ? { expectCredits } : {}) }),
      `confirmed and started ${label(projectId, sceneId, "clip")}${model ? ` with ${model}` : ""}`,
    );
  } catch (e) {
    failed(e, "start the clip");
  }
}

/**
 * fix-produce: the Animate sheet's yes. Never silent: a new price comes back as `reprice` (the sheet asks again),
 * anything else as `error` (the sheet shows it and stays open).
 */
export async function makeClipConfirmed(projectId: string, sceneId: string, model: string | undefined, motionPrompt: string | undefined, expectCredits: number | undefined): Promise<{ kind: "ok"; job: JobView } | PaidFailure> {
  try {
    const job = await api.makeClip(projectId, sceneId, { ...(model ? { model } : {}), ...(motionPrompt ? { motionPrompt } : {}), ...(expectCredits !== undefined ? { expectCredits } : {}) });
    return { kind: "ok", job: track(job, `confirmed and started ${label(projectId, sceneId, "clip")}${model ? ` with ${model}` : ""}`) };
  } catch (e) {
    return paidFailure(e);
  }
}

/** A blocked or failed clip: try the suggested model instead (the tray's "Try Kling 2.1 for 48"). */
export async function tryModel(projectId: string, sceneId: string, model: string, label: string) {
  const job = await makeClip(projectId, sceneId, model);
  if (job) director.context(`The user chose to retry ${labelOf(projectId, sceneId)} with ${label} after the failure (job ${job.id}).`);
  return job;
}
const labelOf = (projectId: string, sceneId: string) => label(projectId, sceneId, "clip");

/** lip sync (2026-10-30): a scene's switch; the Director hears about it, and the clip price reloads with the project. */
export async function setLipSync(projectId: string, sceneId: string, on: boolean) {
  try {
    await api.setLipSync(projectId, sceneId, on);
    bump("project");
    director.context(`The user turned lip sync ${on ? "on" : "off"} for ${label(projectId, sceneId, "clip").replace(/ clip$/, "")} by tapping.`);
    return true;
  } catch (e) {
    failed(e, "change lip sync");
    return false;
  }
}

/** Keaton 2026-10-30: a scene's own picture or video model (independent picks; null = the video's). Free: the price
 *  shows on the next make. The Director hears about it. */
export async function setSceneModel(projectId: string, sceneId: string, step: "image" | "clip", model: string | null, label: string) {
  try {
    await api.setSceneModel(projectId, sceneId, step, model);
    bump("project");
    const scene = label_(projectId, sceneId);
    director.context(`The user set ${scene}'s ${step === "clip" ? "video" : "picture"} model to ${model ? label : `the video's default (${label})`} by tapping. Its ${step === "clip" ? "picture" : "video"} model didn't change.`);
    return true;
  } catch (e) {
    failed(e, "change the model");
    return false;
  }
}
const label_ = (projectId: string, sceneId: string) => label(projectId, sceneId, "clip").replace(/ clip$/, "");

/** lip sync: thumbs on a talking scene's clip (quiet internal feedback on its model; the Director hears it). */
export async function rateLipSync(projectId: string, sceneId: string, verdict: "good" | "bad") {
  try {
    await api.rateLipSync(projectId, sceneId, verdict);
    director.context(`The user rated ${label(projectId, sceneId, "clip")}'s lip sync ${verdict} by tapping (already filed; don't file it again).`);
    toast("Thanks, noted.", "good");
    return true;
  } catch (e) {
    failed(e, "save that");
    return false;
  }
}

export async function approveMusic(projectId: string) {
  try {
    await api.approveMusic(projectId);
    bump("project");
    director.context("The user approved the music bed by tapping.");
    return true;
  } catch (e) {
    failed(e, "approve the music");
    return false;
  }
}

export async function redoMusic(projectId: string, note?: string, expectCredits?: number) {
  try {
    const job = await api.rejectMusic(projectId, note, expectCredits);
    if (job) return track(job, `asked to redo the music bed${note ? ` ("${note}")` : ""}`);
    return makeMusic(projectId, note);
  } catch (e) {
    failed(e, "redo the music");
  }
}

/** Save an effect's nudge, level or mute; returns the updated project. */
export async function editSfx(projectId: string, sceneId: string, edit: SfxEdit, said: string) {
  try {
    const p = await api.patchSfx(projectId, sceneId, [edit]);
    director.context(`The user ${said} on ${label(projectId, sceneId, "sounds")} by tapping.`);
    return p;
  } catch (e) {
    failed(e, "change that sound");
    return null;
  }
}

/** The scene's clip (or still) with its sounds mixed over it, to hear before keeping them (free). Null on failure. */
export async function previewSceneSound(projectId: string, sceneId: string, tweaks?: SfxEdit[], mix?: SoundMix) {
  try {
    const pv = await api.sceneSoundPreview(projectId, sceneId, { ...(tweaks?.length ? { tweaks } : {}), ...(mix ? { mix } : {}) });
    const what = mix === "voice" ? "voice lines" : mix === "sounds" ? "sounds" : "sounds and voice lines";
    director.context(`The user is hearing ${label(projectId, sceneId, what)} over the scene (a free preview, tapped).`);
    return pv;
  } catch (e) {
    failed(e, "make the sound preview");
    return null;
  }
}

// ------------------------------------------------------------------ voices on their own (2026-10-31, api v4/narration.ts)

const whereOf = (projectId: string, sceneIds: string[] | null) =>
  !sceneIds?.length ? "the whole video" : sceneIds.length === 1 ? `scene ${sceneIndex(projectId, sceneIds[0]) ?? ""}`.trim() : `${sceneIds.length} scenes`;

/** Records the missing voice lines (price first: `credits` is the price on the button). No pictures or clips change. */
export async function recordVoices(projectId: string, sceneIds: string[] | null, credits: number) {
  try {
    const r = await api.recordVoices(projectId, sceneIds, credits);
    for (const j of r.jobs) onJob(j);
    bump("credits", "project");
    director.context(`The user started recording the voices for ${whereOf(projectId, sceneIds)} by tapping (${r.plan.newLines} lines, about ${r.plan.credits} credits). Don't start it again.`);
    return r;
  } catch (e) {
    failed(e, "record the voices");
    return null;
  }
}

/** Plays the recorded lines alone (one scene, or every scene in order at the render's timing). Free. */
export async function playVoices(projectId: string, sceneIds: string[] | null) {
  try {
    const a = await api.playVoices(projectId, sceneIds ?? undefined);
    playNow({ url: a.url, what: "audio", title: `The voices, ${whereOf(projectId, sceneIds)}`, projectId, assetId: a.assetId, durationS: a.seconds });
    director.context(`The user is listening to the recorded voices for ${whereOf(projectId, sceneIds)} by tapping (free).${a.missing.length ? ` ${a.missing.length} lines aren't recorded yet and are left out.` : ""}`);
    return a;
  } catch (e) {
    failed(e, "play the voices");
    return null;
  }
}

/** The whole video as a quick pass over its pictures with the recorded lines. Free (daily preview allowance). */
export async function voicePass(projectId: string) {
  try {
    const v = await api.voicePass(projectId);
    playNow({ url: v.url, what: "clip", title: "The voices over the pictures (quick pass)", projectId, assetId: v.assetId, durationS: v.seconds });
    director.context("The user is watching the quick pass of the voices over the pictures by tapping (free).");
    return v;
  } catch (e) {
    failed(e, "make the quick pass");
    return null;
  }
}

/** Moves, splits, adds, removes or reorders a scene's lines (free, undoable; recorded takes go with their lines). */
export async function editLines(projectId: string, body: LinesEdit, said: string) {
  try {
    const r = await api.editLines(projectId, body);
    bump("project");
    director.context(`The user ${said} by tapping (free; it's in the change history).${r.clipsSetAside.length ? ` Scene ${r.clipsSetAside.join(", ")}'s clip was made for the old lines and is set aside.` : ""}`);
    if (r.clipsSetAside.length) toast(`Scene ${r.clipsSetAside.join(", ")}'s clip was made for the old lines, so it's set aside (still in your library).`);
    return r;
  } catch (e) {
    failed(e, "change the lines");
    return null;
  }
}

/** The scene's lip-sync lead-in (seconds of silence before the first character line). Free. */
export async function setLipLead(projectId: string, sceneId: string, seconds: number) {
  try {
    await api.setLipLead(projectId, sceneId, seconds);
    bump("project");
    director.context(`The user set ${label_(projectId, sceneId)}'s lip-sync lead-in to ${seconds} seconds by tapping.`);
    return true;
  } catch (e) {
    failed(e, "set the lead-in");
    return false;
  }
}

export async function makeSounds(projectId: string, sceneId: string) {
  try {
    return track(await api.makeSounds(projectId, sceneId), `started ${label(projectId, sceneId, "sounds")}`);
  } catch (e) {
    failed(e, "start the sounds");
  }
}

export async function makeMusic(projectId: string, prompt?: string) {
  try {
    return track(await api.makeMusic(projectId, prompt ? { prompt } : {}), `started the music bed${prompt ? ` ("${prompt}")` : ""}`);
  } catch (e) {
    failed(e, "start the music");
  }
}

export async function produce(projectId: string, mode: ProduceMode) {
  const r = await produceConfirmed(projectId, mode);
  if (r.kind === "ok") return r.jobs;
  toast(r.kind === "reprice" ? `${r.text} Open it again to say yes.` : `Couldn't start that. ${r.text}`, "bad");
}

/**
 * fix-produce: the produce sheet's yes ("Go for 19"). Never silent: a plan that costs more than the yes comes back as
 * `reprice` with the new price (the sheet asks again), any other failure as `error` (the sheet shows it, stays open).
 */
export async function produceConfirmed(projectId: string, mode: ProduceMode): Promise<{ kind: "ok"; jobs: JobView[] } | PaidFailure> {
  try {
    const jobs = await api.produce(projectId, mode);
    jobs.forEach((j) => onJob(j));
    bump("credits", "project", "next");
    const total = jobs.reduce((a, j) => a + j.credits, 0);
    director.context(`The user tapped produce (${mode.scope}${mode.sceneId ? ` ${mode.sceneId}` : ""}, through ${mode.through}): ${jobs.length} jobs started, about ${total} credits. Don't start them again.`);
    return { kind: "ok", jobs };
  } catch (e) {
    const f = paidFailure(e);
    // the balance or the plan may have moved: the screens re-read them
    bump("credits", "next");
    return f;
  }
}

export async function approve(projectId: string, sceneId: string, what: "frame" | "clip") {
  try {
    await api.approve(projectId, sceneId, what);
    bump("project");
    director.context(`The user approved ${label(projectId, sceneId, what)} by tapping.`);
    return true;
  } catch (e) {
    failed(e, `approve the ${what}`);
    return false;
  }
}

/** 2026-11-03: throw away a take waiting for review ("Scratch it"); an earlier take comes back. Free, undoable. */
export async function scratch(projectId: string, sceneId: string, what: "frame" | "clip") {
  try {
    const r = await api.scratch(projectId, sceneId, what);
    bump("project");
    director.context(`The user scratched ${label(projectId, sceneId, what)} by tapping (free${r.restored ? `; the ${what} it had before is back` : `; the scene has no ${what} now`}).`);
    toast(r.restored ? `Scratched. The ${what} it had before is back.` : "Scratched.", "good");
    return true;
  } catch (e) {
    failed(e, `scratch the ${what}`);
    return false;
  }
}

/** The scene's clip's last frame becomes a pinned reference for the next scene's frame (free). */
export async function useLastFrame(projectId: string, sceneId: string, toSceneId?: string) {
  try {
    const r = await api.lastFrame(projectId, sceneId, toSceneId);
    bump("project");
    toast(`Scene ${r.fromScene}'s last frame is now a reference for scene ${r.toScene}.`, "good");
    director.context(`The user tapped to use scene ${r.fromScene}'s clip's last frame as a pinned reference picture for scene ${r.toScene}'s next frame (free, nothing remade).`);
    return r;
  } catch (e) {
    failed(e, "use the last frame");
    return null;
  }
}

/** 2026-11-02: the picture a scene's next clip ends on (a reveal), or null to clear it. Free. */
export async function setClipEnd(projectId: string, sceneId: string, picture: { id: string; name: string } | null) {
  try {
    const r = await api.setClipEnd(projectId, sceneId, picture?.id ?? null);
    bump("project");
    const n = label(projectId, sceneId, "clip");
    if (!picture) toast("The clip no longer ends on a set picture.", "good");
    else if (r.modelTakesIt) toast(`The next clip ends on "${picture.name}".`, "good");
    else toast(`${r.modelLabel} can't end on a picture. Pick ${r.models.slice(0, 3).join(", ") || "another model"} under Motion and model.`, "bad");
    director.context(picture ? `The user tapped to make ${n} end on the picture "${picture.name}" (free, nothing made yet${r.modelTakesIt ? "" : `; ${r.modelLabel} can't end on a picture, ${r.models.join(", ")} can`}).` : `The user cleared the picture ${n} ends on.`);
    return r;
  } catch (e) {
    failed(e, "set the picture the clip ends on");
    return null;
  }
}

/** 2026-11-03: a scene's fit: its clip stretched to its voice ("video"), its voice to its clip ("audio"), or automatic. */
export async function setSceneFit(projectId: string, sceneId: string, fit: SceneFit | null) {
  try {
    const r = await api.setSceneFit(projectId, sceneId, fit);
    bump("project");
    const i = sceneIndex(projectId, sceneId);
    const n = i ? `scene ${i}` : "the scene";
    const how = r.speed && r.speed !== 1 ? ` (${r.fit === "video" ? "the clip" : "the voice"} at ${r.speed}×)` : "";
    toast(!fit ? "The clip is trimmed to the voice." : fit === "video" ? `The clip will stretch to the voice${how}.` : `The voice will stretch to the clip${how}.`, "good");
    if (r.clip?.lipSynced && fit) toast("This clip is lip-synced, so it isn't stretched (the mouths would drift).", "bad");
    director.context(fit ? `The user tapped to fit ${n}: ${fit === "video" ? "its clip stretched to its voice" : "its voice stretched to its clip (pitch kept)"}${how}. Free; from the next render.` : `The user set ${n} back to trimming its clip to its voice (the default: the clip cut where the line ends).`);
    return r;
  } catch (e) {
    failed(e, "change the scene's timing");
    return null;
  }
}

/** Redo a frame or clip: reject the take (with the note), then start the new one unless the server already did. */
export async function redoShot(projectId: string, sceneId: string, what: "frame" | "clip", note?: string, model?: string, expectCredits?: number) {
  try {
    // cost audit + PRICE-FIRST: a redo carries the price shown with it (expectCredits); a dearer one is refused for a new yes
    let job = await api.reject(projectId, sceneId, what, note, expectCredits);
    if (!job) job = what === "frame" ? await api.makeFrame(projectId, sceneId, expectCredits !== undefined ? { expectCredits } : {}) : await api.makeClip(projectId, sceneId, { ...(model ? { model } : {}), ...(expectCredits !== undefined ? { expectCredits } : {}) });
    return track(job, `asked to redo ${label(projectId, sceneId, what)}${note ? ` ("${note}")` : ""}`);
  } catch (e) {
    failed(e, `redo the ${what}`);
  }
}

// ------------------------------------------------------------------ price first

/** What a paid tap's yes came to: started, or (never silent) a new price to ask about again, or what went wrong. */
export type PaidOutcome = { kind: "ok"; job: JobView | null; project?: Project } | PaidFailure;
export type StepOpts = { sceneId?: string; redo?: boolean; note?: string; prompt?: string; model?: string; motionPrompt?: string };

/**
 * PRICE-FIRST: start (or redo, or try again) one paid step at the price the user saw, `expectCredits`. The server
 * refuses a dearer one (CONFIRM_REQUIRED), which comes back as `reprice` for the sheet to ask again; nothing is
 * charged then. Every tap that costs credits on the shot board, the scene detail and the review tray goes through here.
 */
export async function startStep(projectId: string, step: Exclude<PriceStep, "suno">, o: StepOpts, expectCredits: number | undefined): Promise<PaidOutcome> {
  const sid = o.sceneId;
  const scene = (what: string) => label(projectId, sid, what);
  const exp = expectCredits !== undefined ? { expectCredits } : {};
  try {
    let job: JobView | null = null;
    let said = "";
    if (step === "script") {
      const project = await api.writeScript(projectId, o.note, expectCredits);
      bump("project", "credits");
      director.context(o.note ? `The user asked for a rewrite ("${o.note}") by tapping.` : "The user wrote the script by tapping. It's written; read the hook back if they ask.");
      return { kind: "ok", job: null, project };
    }
    if (step === "music") {
      job = o.redo ? ((await api.rejectMusic(projectId, o.note, expectCredits)) ?? (await api.makeMusic(projectId, { ...(o.note ? { prompt: o.note } : {}), ...exp }))) : await api.makeMusic(projectId, { ...(o.prompt ? { prompt: o.prompt } : {}), ...exp });
      said = o.redo ? `asked to redo the music bed${o.note ? ` ("${o.note}")` : ""}` : "started the music bed";
    } else {
      if (!sid) throw new ApiError("Which scene?", 400);
      if (step === "frame") {
        job = o.redo ? ((await api.reject(projectId, sid, "frame", o.note, expectCredits)) ?? (await api.makeFrame(projectId, sid, exp))) : await api.makeFrame(projectId, sid, { ...(o.prompt ? { prompt: o.prompt } : {}), ...exp });
        said = o.redo ? `asked to redo ${scene("frame")}${o.note ? ` ("${o.note}")` : ""}` : `started ${scene("frame")}`;
      } else if (step === "clip") {
        const clip = { ...(o.model ? { model: o.model } : {}), ...(o.motionPrompt ? { motionPrompt: o.motionPrompt } : {}), ...exp };
        job = o.redo ? ((await api.reject(projectId, sid, "clip", o.note, expectCredits)) ?? (await api.makeClip(projectId, sid, clip))) : await api.makeClip(projectId, sid, clip);
        said = `${o.redo ? "asked to redo" : "confirmed and started"} ${scene("clip")}${o.model ? ` with ${o.model}` : ""}`;
      } else {
        job = await api.makeSounds(projectId, sid, expectCredits);
        said = `started ${scene("sounds")}`;
      }
    }
    return { kind: "ok", job: job ? track(job, `${said} at ${expectCredits ?? job.credits} credits`) : null };
  } catch (e) {
    bump("credits");
    return paidFailure(e);
  }
}

// ------------------------------------------------------------------ cards

export async function approveArtifact(a: Artifact) {
  if ((a.kind === "frame" || a.kind === "clip") && a.sceneId) {
    if (await approve(a.projectId, a.sceneId, a.kind)) showArtifact({ ...a, status: "approved", actions: (a.actions ?? []).filter((x) => x !== "approve") });
    return;
  }
  // scripts and other cards: approval is the go-ahead in the conversation
  showArtifact({ ...a, status: "approved", actions: (a.actions ?? []).filter((x) => x !== "approve") });
  director.context(`The user approved "${a.title}" by tapping. Carry on to the next step.`);
}

/** PRICE-FIRST: `expectCredits` is the price the Redo sheet showed; a dearer redo comes back as `reprice`. */
export async function redoArtifact(a: Artifact, note?: string, expectCredits?: number): Promise<PaidOutcome | void> {
  if ((a.kind === "frame" || a.kind === "clip") && a.sceneId) {
    const r = await startStep(a.projectId, a.kind, { sceneId: a.sceneId, redo: true, note }, expectCredits);
    if (r.kind === "ok" && r.job) showArtifact({ ...a, status: "working", jobId: r.job.id, progress: 0, note: r.job.step ?? "Starting" });
    return r;
  }
  if (a.kind === "script") return startStep(a.projectId, "script", { note: note || "Try a different take." }, expectCredits);
  if (a.kind === "audio" && a.voiceId) {
    await tapTool("voice_sample", { voice_id: a.voiceId, text: a.text ?? "", style: note ?? "" }, `asked ${voiceNameOf(a)} to read it again${note ? ` (${note})` : ""}`);
    return;
  }
  if (a.kind === "audio") {
    const r = await startStep(a.projectId, a.sceneId ? "sfx" : "music", { sceneId: a.sceneId, redo: true, note }, expectCredits);
    if (r.kind === "ok" && r.job) showArtifact({ ...a, status: "working", jobId: r.job.id, progress: 0 });
    return r;
  }
  director.context(`The user asked to redo "${a.title}" by tapping${note ? `: "${note}"` : ""}.`);
}

export function playArtifact(a: Artifact, key: string) {
  if (a.kind === "audio") return toggleAudio(key, a.url);
  if (!a.url) return toast("That isn't ready to play yet.", "bad");
  playNow({ url: a.url, title: a.title, what: a.kind === "render" ? "render" : "clip", projectId: a.projectId, sceneId: a.sceneId, assetId: a.assetId });
}

/** "Brian reads your hook (excited)" -> "Brian" */
export const voiceNameOf = (a: Pick<Artifact, "title">) => a.title.trim().split(/[\s,:(–-]/)[0] || "that voice";

/**
 * "Use this voice" on a voice sample: the Director's own voice switches live (set_director_voice returns the
 * switch directive); a narrator goes on the open ad, or becomes the brand's default.
 */
export async function useVoice(a: Artifact) {
  if (!a.voiceId) return;
  const name = a.voiceName || voiceNameOf(a);
  if (a.voiceFor === "director") {
    const r = await tapTool("set_director_voice", { voice_id: a.voiceId }, `picked ${name} as the Director's voice`);
    // an API that saved the voice without sending the directive: switch anyway
    if (r && !JSON.stringify(r.ui ?? null).includes("switch_director_voice")) void switchVoice(a.voiceId, name);
    return;
  }
  const projectId = a.projectId || openProjectId();
  if (projectId) await tapTool("set_narrator", { project_id: projectId, voice_id: a.voiceId, voice_name: name }, `picked ${name} to narrate this ad`);
  else await tapTool("set_brand", { narrator_voice_id: a.voiceId, narrator_name: name }, `picked ${name} as the brand's narrator`);
  toast(`${name} will narrate.`, "good");
}

/**
 * Call a Director tool on a tap (voices have no REST route of their own: the tool endpoint is the shared
 * function). Cards it returns go to the chat unless `keepCards` is set, when they're returned for the screen.
 */
export async function tapTool(name: string, args: Record<string, unknown>, said: string, opts: { keepCards?: boolean } = {}) {
  try {
    const r = await api.directorTool(name, args, { projectId: openProjectId() });
    if (opts.keepCards) await applyUi(withoutCardsOrStage(r.ui));
    else await applyUi(r.ui);
    director.context(`The user ${said} by tapping.`);
    return { ...r, cards: artifactsOf(r.ui) };
  } catch (e) {
    failed(e, "do that");
    return null;
  }
}
